import { useRef, useState } from "react";
import { ref, uploadBytes } from "firebase/storage";
import { storage } from "../lib/firebase";
import { api, callableErrorMessage } from "../lib/api";
import { track } from "../lib/analytics";

type Props = {
  tenantId: string;
  uid: string;
  existingIdentifiers?: string[];
  existingTaxIdHints?: string[];
  existingProducts?: string[];
  existingPrices?: number[];
  onSaved?: () => void;
};

type Detected = {
  identifiers: string[];
  taxIds: string[];
  amounts: number[];
  products: string[];
  addresses: string[];
  ocrText: string;
};

/**
 * Componente reutilizable que:
 *  1) Permite al tenant_admin subir una boleta de muestra.
 *  2) La analiza con Vision OCR + extractor.
 *  3) Muestra campos detectados editables (chips).
 *  4) Permite confirmar y guarda en config/ocr (merge con lo existente).
 */
export function OcrTrainer({
  tenantId,
  uid,
  existingIdentifiers = [],
  existingTaxIdHints = [],
  existingProducts = [],
  existingPrices = [],
  onSaved
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<"upload" | "review" | "done">("upload");
  const [error, setError] = useState<string | null>(null);
  const [detected, setDetected] = useState<Detected>({
    identifiers: [...existingIdentifiers],
    taxIds: [...existingTaxIdHints],
    amounts: [...existingPrices],
    products: [...existingProducts],
    addresses: [],
    ocrText: ""
  });

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setPreview(URL.createObjectURL(f));
      analyze(f);
    }
  };

  const analyze = async (file: File) => {
    setBusy(true);
    setError(null);
    track("ocr_trainer_analyze_started");
    try {
      const ts = Date.now();
      const ext = (file.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const path = `tenants/${tenantId}/samples/${uid}/${ts}.${ext}`;
      await uploadBytes(ref(storage, path), file, { contentType: file.type });

      const result = await api.analyzeSampleReceipt({ storagePath: path });
      track("ocr_trainer_analyze_success");

      // Merge con valores existentes para no perder lo previamente confirmado
      const allLegal = unique([...existingIdentifiers, ...result.data.detectedLegalNames.map(s => s.toLowerCase())]);
      const allAddresses = unique([...existingIdentifiers.filter(i => i.includes(" ")), ...result.data.detectedAddresses.map(s => s.toLowerCase())]);
      const merged: Detected = {
        identifiers: unique([...allLegal, ...allAddresses]),
        taxIds: unique([...existingTaxIdHints, ...result.data.detectedTaxIds]),
        amounts: unique([...existingPrices, ...result.data.detectedAmounts]).sort((a, b) => a - b),
        products: unique([...existingProducts, ...result.data.detectedProducts.map(s => s.toLowerCase())]),
        addresses: result.data.detectedAddresses,
        ocrText: result.data.ocrText
      };
      setDetected(merged);
      setPhase("review");
    } catch (err) {
      track("ocr_trainer_analyze_failed", { code: (err as { code?: string })?.code ?? "unknown" });
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = <T,>(list: T[], item: T): T[] =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateTenantOcrConfig({
        tenantId,
        config: {
          identifiers: detected.identifiers,
          taxIdHints: detected.taxIds,
          knownProducts: detected.products,
          validPrices: detected.amounts
        }
      });
      track("ocr_trainer_saved", {
        identifiers: detected.identifiers.length,
        taxIds: detected.taxIds.length,
        products: detected.products.length,
        prices: detected.amounts.length
      });
      setPhase("done");
      onSaved?.();
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "done") {
    return (
      <div className="rounded-2xl bg-leaf-50 p-4 ring-1 ring-leaf-200">
        <p className="text-sm font-semibold text-leaf-900">✓ Configuracion guardada.</p>
        <p className="mt-1 text-xs text-leaf-700">
          A partir de ahora, las boletas de tus clientes se van a validar contra estos datos.
          Las boletas duplicadas se rechazan automáticamente (gracias al folio + monto + tenant).
        </p>
        <button onClick={() => { setPhase("upload"); setPreview(null); }} className="btn-ghost mt-2 text-xs">
          Cargar otra boleta para enriquecer
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {phase === "upload" && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={pick}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/50 py-6 text-leaf-700 hover:bg-leaf-50 disabled:opacity-50"
          >
            {busy ? (
              <span className="text-sm font-medium">Leyendo tu boleta…</span>
            ) : (
              <>
                <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span className="text-sm font-medium">Sube tu boleta de ejemplo</span>
                <span className="text-xs text-leaf-500">JPG o PNG · máx 10 MB</span>
              </>
            )}
          </button>
          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </>
      )}

      {phase === "review" && (
        <>
          {preview && (
            <div className="overflow-hidden rounded-2xl ring-1 ring-leaf-100">
              <img src={preview} alt="Boleta" className="block max-h-48 w-full object-cover" />
            </div>
          )}

          <p className="text-xs text-leaf-700">
            Detectamos esto en tu boleta. Toca los chips para activar/desactivar lo que es real.
            Vamos a usar estos datos para validar las boletas que suban tus clientes.
          </p>

          <Section title="RUT del negocio">
            <ChipGroup
              all={[...new Set([...detected.taxIds, ...existingTaxIdHints])]}
              selected={detected.taxIds}
              onToggle={(id) => setDetected({ ...detected, taxIds: toggle(detected.taxIds, id) })}
            />
          </Section>

          <Section title="Nombres / direcciones que aparecen en la boleta">
            <ChipGroup
              all={[...new Set([...detected.identifiers, ...detected.addresses.map(a => a.toLowerCase())])]}
              selected={detected.identifiers}
              onToggle={(id) => setDetected({ ...detected, identifiers: toggle(detected.identifiers, id) })}
            />
          </Section>

          <Section title="Productos detectados">
            <ChipGroup
              all={[...new Set(detected.products)]}
              selected={detected.products}
              onToggle={(p) => setDetected({ ...detected, products: toggle(detected.products, p) })}
            />
          </Section>

          <Section title="Precios típicos">
            <ChipGroup
              all={[...new Set(detected.amounts)]}
              selected={detected.amounts}
              onToggle={(a) => setDetected({ ...detected, amounts: toggle(detected.amounts, a) })}
              formatter={(n) => `$${(n as number).toLocaleString("es-CL")}`}
            />
          </Section>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={() => { setPhase("upload"); setPreview(null); }} disabled={busy} className="btn-ghost flex-1">
              Volver
            </button>
            <button onClick={save} disabled={busy || detected.identifiers.length + detected.taxIds.length === 0} className="btn-primary flex-1">
              {busy ? "Guardando…" : "Confirmar y guardar"}
            </button>
          </div>

          <p className="text-[11px] text-leaf-500">
            Necesitas marcar al menos un identificador o RUT para que la validación funcione.
          </p>
        </>
      )}
    </div>
  );
}

function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs uppercase tracking-wider text-leaf-600">{title}</p>
      {children}
    </div>
  );
}

function ChipGroup<T extends string | number>({
  all,
  selected,
  onToggle,
  formatter
}: {
  all: T[];
  selected: T[];
  onToggle: (v: T) => void;
  formatter?: (v: T) => string;
}) {
  if (all.length === 0) {
    return <p className="text-xs italic text-leaf-500">No detectado. Carga otra boleta para enriquecer.</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {all.map((v) => {
        const isSelected = selected.includes(v);
        const label = formatter ? formatter(v) : String(v);
        return (
          <button
            key={String(v)}
            type="button"
            onClick={() => onToggle(v)}
            className={`rounded-full border-2 px-3 py-1.5 text-xs font-medium transition ${
              isSelected
                ? "border-leaf-700 bg-leaf-700 text-white"
                : "border-leaf-200 bg-white text-leaf-700 hover:bg-leaf-50"
            }`}
          >
            {isSelected ? "✓ " : ""}{label}
          </button>
        );
      })}
    </div>
  );
}
